<template>
    <div class="privacy-page">
        <!-- Header -->
        <header class="privacy-header">
            <div class="header-content">
                <a href="https://admin.shieldgo.com.br/" class="header-logo">
                    <img src="../../assets/images/logo.png" alt="ShieldGo" width="140" />
                </a>
                <div class="language-selector">
                    <button :class="{ active: language === 'pt' }" @click="changeLanguage('pt')" title="Portugues">
                        <span class="flag-icon flag-icon-br"></span>
                        PT
                    </button>
                    <button :class="{ active: language === 'en' }" @click="changeLanguage('en')" title="English">
                        <span class="flag-icon flag-icon-us"></span>
                        EN
                    </button>
                    <button :class="{ active: language === 'es' }" @click="changeLanguage('es')" title="Español">
                        <span class="flag-icon flag-icon-es"></span>
                        ES
                    </button>
                </div>
            </div>
        </header>

        <!-- Content -->
        <main class="privacy-main">
            <div class="privacy-container">
                <!-- Title section -->
                <div class="privacy-title-section">
                    <h1 v-if="language === 'en'">Privacy Policy</h1>
                    <h1 v-else-if="language === 'es'">Pol&iacute;tica de Privacidad</h1>
                    <h1 v-else>Politica de Privacidade</h1>
                    <p class="last-updated" v-if="language === 'en'">Last updated November 21, 2023</p>
                    <p class="last-updated" v-else-if="language === 'es'">&Uacute;ltima actualizaci&oacute;n 21 de noviembre de 2023</p>
                    <p class="last-updated" v-else>Ultima atualizacao em 21 de novembro de 2023</p>
                </div>

                <!-- English Content -->
                <div v-if="language === 'en'" class="privacy-content">
                    <section class="privacy-section">
                        <p>
                            This privacy notice for Grasp Sistemas e Comercio Ltda (doing business as Grasp) ("we," "us," or "our"), describes how and why we might collect, store, use, and/or share ("process") your
                            information when you use our services ("Services"), such as when you:
                        </p>
                        <ul>
                            <li>Visit our website at <a href="https://shieldgo.com.br/" target="_blank">https://shieldgo.com.br</a>, or any website of ours that links to this privacy notice</li>
                            <li>Download and use our mobile application (ShieldGo), or any other application of ours that links to this privacy notice</li>
                            <li>Engage with us in other related ways, including any sales, marketing, or events</li>
                        </ul>
                        <p>
                            Questions or concerns? Reading this privacy notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our
                            Services. If you still have any questions or concerns, please contact us at <a href="mailto:privacy@shieldgo.com.br">privacy@shieldgo.com.br</a>.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>Summary of Key Points</h2>
                        <div class="key-points">
                            <ol>
                                <li><strong>What personal information do we process?</strong> Learn more about personal information you disclose to us.</li>
                                <li><strong>Do we process any sensitive personal information?</strong> Learn more about sensitive information we process.</li>
                                <li><strong>Do we receive any information from third parties?</strong> Learn more about information collected from other sources.</li>
                                <li><strong>How do we process your information?</strong> Learn more about how we process your information.</li>
                                <li><strong>In what situations and with which parties do we share personal information?</strong> Learn more about when and with whom we share your personal information.</li>
                                <li><strong>How do we keep your information safe?</strong> Learn more about how we keep your information safe.</li>
                                <li><strong>What are your rights?</strong> Learn more about your privacy rights.</li>
                                <li><strong>How do you exercise your rights?</strong> Learn more about how to exercise your privacy rights.</li>
                                <li><strong>Want to learn more about what we do with any information we collect?</strong> Review the privacy notice in full.</li>
                            </ol>
                        </div>
                    </section>

                    <section class="privacy-section">
                        <h2>Table of Contents</h2>
                        <nav class="toc">
                            <ol>
                                <li>What information do we collect?</li>
                                <li>How do we process your information?</li>
                                <li>What legal bases do we rely on to process your personal information?</li>
                                <li>When and with whom do we share your personal information?</li>
                                <li>Do we use cookies and other tracking technologies?</li>
                                <li>How long do we keep your information?</li>
                                <li>How do we keep your information safe?</li>
                                <li>Do we collect information from minors?</li>
                                <li>What are your privacy rights?</li>
                                <li>Controls for do-not-track features</li>
                                <li>Do United States residents have specific privacy rights?</li>
                                <li>Do we make updates to this notice?</li>
                                <li>How can you contact us about this notice?</li>
                                <li>How can you review, update, or delete the data we collect from you?</li>
                            </ol>
                        </nav>
                    </section>

                    <section class="privacy-section">
                        <h2>1. What Information Do We Collect?</h2>
                        <h3>Personal information you disclose to us</h3>
                        <p>
                            We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services,
                            when you participate in activities on the Services, or otherwise when you contact us.
                        </p>
                        <p>
                            <strong>Personal Information Provided by You.</strong> The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and
                            the products and features you use. The personal information we collect may include the following:
                        </p>
                        <ul>
                            <li>Names</li>
                            <li>Phone numbers</li>
                            <li>Email addresses</li>
                            <li>Usernames</li>
                            <li>Passwords</li>
                            <li>Debit/credit card numbers</li>
                            <li>Billing addresses</li>
                        </ul>
                        <p><strong>Sensitive Information.</strong> When necessary, with your consent or as otherwise permitted by applicable law, we process the following categories of sensitive information:</p>
                        <ul>
                            <li>Social security numbers or other government identifiers</li>
                        </ul>
                        <p>
                            <strong>Payment Data.</strong> We may collect data necessary to process your payment if you make purchases, such as your payment instrument number, and the security code associated with your
                            payment instrument. All payment data is stored by EBANX. You may find their privacy notice link(s) here:
                            <a href="https://shieldgo.com.br/?#/payment-privacy-policy" target="_blank">https://shieldgo.com.br/?#/payment-privacy-policy</a>.
                        </p>
                        <p><strong>Application Data.</strong> If you use our application(s), we also may collect the following information if you choose to provide us with access or permission:</p>
                        <ul>
                            <li>Geolocation Information</li>
                            <li>Mobile Device Access</li>
                            <li>Mobile Device Data</li>
                            <li>Push Notifications</li>
                        </ul>
                        <p>This information is primarily needed to maintain the security and operation of our application(s), for troubleshooting, and for our internal analytics and reporting purposes.</p>
                        <p>All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.</p>

                        <h3>Information automatically collected</h3>
                        <p>Some information &mdash; such as your Internet Protocol (IP) address and/or browser and device characteristics &mdash; is collected automatically when you visit our Services.</p>
                        <p>
                            We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity (like your name or contact
                            information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device
                            name, country, location, information about how and when you use our Services, and other technical information. This information is primarily needed to maintain the security and operation
                            of our Services, and for our internal analytics and reporting purposes.
                        </p>
                        <p>Like many businesses, we also collect information through cookies and similar technologies.</p>
                        <p>The information we collect includes:</p>
                        <ul>
                            <li>Log and Usage Data</li>
                            <li>Device Data</li>
                            <li>Location Data</li>
                        </ul>

                        <h3>Information collected from other sources</h3>
                        <p>
                            In order to enhance our ability to provide relevant marketing, offers, and services to you and update our records, we may obtain information about you from other sources, such as public
                            databases, joint marketing partners, affiliate programs, data providers, and from other third parties.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>2. How Do We Process Your Information?</h2>
                        <p>We process your personal information for a variety of reasons, depending on how you interact with our Services. These reasons include:</p>
                        <ul>
                            <li>To facilitate account creation and authentication and otherwise manage user accounts.</li>
                            <li>To deliver and facilitate delivery of services to the user.</li>
                            <li>To send administrative information to you.</li>
                            <li>To enable user-to-user communications.</li>
                            <li>To protect our Services.</li>
                            <li>To save or protect an individual's vital interest.</li>
                            <li>Geolocation and Device Information for application optimization and performance improvement.</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>3. What Legal Bases Do We Rely On To Process Your Information?</h2>
                        <p>
                            We only process your personal information when we believe it is necessary and we have a valid legal reason (i.e., legal basis) to do so under applicable law, like with your consent, to
                            comply with laws, to provide you with services to enter into or fulfill our contractual obligations, to protect your rights, or to fulfill our legitimate business interests.
                        </p>
                        <p>The legal bases we rely on to process your personal information include:</p>
                        <ul>
                            <li>Consent</li>
                            <li>Performance of a Contract</li>
                            <li>Legitimate Interests</li>
                            <li>Legal Obligations</li>
                            <li>Vital Interests</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>4. When And With Whom Do We Share Your Personal Information?</h2>
                        <p>We may need to share your personal information in specific situations described in this section and/or with the following third parties:</p>
                        <ul>
                            <li><strong>With service providers:</strong> To facilitate our services; to provide services on our behalf; to perform service-related services; or to assist us in analyzing how our services are used.</li>
                            <li><strong>With other users:</strong> When you share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside.</li>
                            <li><strong>With affiliates:</strong> To our affiliates for the purposes described in this Privacy Policy.</li>
                            <li><strong>With business partners:</strong> With trusted business partners who may use your personal information to contact you about related products and services.</li>
                            <li><strong>With other third parties:</strong> With other third parties for purposes for which we have obtained your consent, and as otherwise described in this Privacy Policy.</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>5. Do We Use Cookies And Other Tracking Technologies?</h2>
                        <p>We use cookies and similar tracking technologies to collect and use personal information about you, including to serve interest-based advertising.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>6. How Long Do We Keep Your Information?</h2>
                        <p>We retain your personal information for as long as is necessary to fulfill the purposes outlined in this Privacy Policy unless a longer retention period is required or permitted by law.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>7. How Do We Keep Your Information Safe?</h2>
                        <p>We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>8. Do We Collect Information From Minors?</h2>
                        <p>
                            We do not knowingly solicit data from or market to children under 18 years of age. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of
                            such a minor and consent to such minor dependent&rsquo;s use of the Services.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>9. What Are Your Privacy Rights?</h2>
                        <p>
                            In some regions, you have certain rights under applicable data protection laws. These may include the right (i) to request access and obtain a copy of your personal information, (ii) to
                            request rectification or erasure, (iii) to restrict the processing of your personal information, and (iv) if applicable, to data portability.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>10. Controls For Do-Not-Track Features</h2>
                        <p>
                            Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to
                            have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As
                            such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>11. Do United States Residents Have Specific Privacy Rights?</h2>
                        <p>California Privacy Rights.</p>
                        <ul>
                            <li>If you are under 18 years of age, reside in California, and have a registered account with the Services, you have the right to request removal of unwanted data that you publicly post on the Services.</li>
                            <li>To request removal of such data, please contact us using the contact information provided below, and include the email address associated with your account and a statement that you reside in California.</li>
                            <li>We will make sure the data is not publicly displayed on the Services, but please be aware that the data may not be completely or comprehensively removed from our systems.</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>12. Do We Make Updates To This Notice?</h2>
                        <p>We may update this privacy notice from time to time. The updated version will be indicated by an updated "Revised" date and the updated version will be effective as soon as it is accessible.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>13. How Can You Contact Us About This Notice?</h2>
                        <p>
                            If you have questions or comments about this notice, you may contact our Data Protection Officer (DPO), [DPO Name], by email at <a href="mailto:privacy@shieldgo.com.br">privacy@shieldgo.com.br</a> or by post to:
                        </p>
                        <div class="contact-card">
                            <p>Grasp Sistemas e Comercio Ltda</p>
                            <p>Attn: Data Protection Officer</p>
                            <p>Rua Rui Barbosa, 123</p>
                            <p>Sao Paulo, SP 01026-000</p>
                            <p>Brazil</p>
                        </div>
                    </section>

                    <section class="privacy-section">
                        <h2>14. How Can You Review, Update, Or Delete The Data We Collect From You?</h2>
                        <p>
                            Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you, change that information, or delete it. To request
                            to review, update, or delete your personal information, please fill out and submit a data subject access request.
                        </p>
                    </section>
                </div>

                <!-- Spanish Content -->
                <div v-else-if="language === 'es'" class="privacy-content">
                    <section class="privacy-section">
                        <p>
                            Este aviso de privacidad de Grasp Sistemas e Com&eacute;rcio Ltda (que opera como Grasp) ("nosotros", "nos" o "nuestro"), describe c&oacute;mo y por qu&eacute;
                            podemos recopilar, almacenar, usar y/o compartir ("procesar") su informaci&oacute;n cuando utiliza nuestros servicios ("Servicios"), como cuando usted:
                        </p>
                        <ul>
                            <li>Visita nuestro sitio web en <a href="https://shieldgo.com.br/" target="_blank">https://shieldgo.com.br</a>, o cualquier sitio web nuestro que enlace a este aviso de privacidad</li>
                            <li>Descarga y utiliza nuestra aplicaci&oacute;n m&oacute;vil (ShieldGo), o cualquier otra aplicaci&oacute;n nuestra que enlace a este aviso de privacidad</li>
                            <li>Interact&uacute;a con nosotros de otras maneras relacionadas, incluyendo ventas, marketing o eventos</li>
                        </ul>
                        <p>
                            &iquest;Preguntas o inquietudes? Leer este aviso de privacidad le ayudar&aacute; a comprender sus derechos y opciones de privacidad. Si no est&aacute; de acuerdo con nuestras
                            pol&iacute;ticas y pr&aacute;cticas, por favor no utilice nuestros Servicios. Si a&uacute;n tiene preguntas o inquietudes, cont&aacute;ctenos en
                            <a href="mailto:privacy@shieldgo.com.br">privacy@shieldgo.com.br</a>.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>Resumen de Puntos Clave</h2>
                        <div class="key-points">
                            <ol>
                                <li><strong>&iquest;Qu&eacute; informaci&oacute;n personal procesamos?</strong> Conozca m&aacute;s sobre la informaci&oacute;n personal que nos proporciona.</li>
                                <li><strong>&iquest;Procesamos alguna informaci&oacute;n personal sensible?</strong> Conozca m&aacute;s sobre la informaci&oacute;n sensible que procesamos.</li>
                                <li><strong>&iquest;Recibimos informaci&oacute;n de terceros?</strong> Conozca m&aacute;s sobre la informaci&oacute;n recopilada de otras fuentes.</li>
                                <li><strong>&iquest;C&oacute;mo procesamos su informaci&oacute;n?</strong> Conozca m&aacute;s sobre c&oacute;mo procesamos su informaci&oacute;n.</li>
                                <li><strong>&iquest;En qu&eacute; situaciones y con qu&eacute; partes compartimos informaci&oacute;n personal?</strong> Conozca m&aacute;s sobre cu&aacute;ndo y con qui&eacute;n compartimos su informaci&oacute;n personal.</li>
                                <li><strong>&iquest;C&oacute;mo mantenemos segura su informaci&oacute;n?</strong> Conozca m&aacute;s sobre c&oacute;mo mantenemos segura su informaci&oacute;n.</li>
                                <li><strong>&iquest;Cu&aacute;les son sus derechos?</strong> Conozca m&aacute;s sobre sus derechos de privacidad.</li>
                                <li><strong>&iquest;C&oacute;mo ejerce sus derechos?</strong> Conozca m&aacute;s sobre c&oacute;mo ejercer sus derechos de privacidad.</li>
                                <li><strong>&iquest;Desea saber m&aacute;s sobre lo que hacemos con la informaci&oacute;n que recopilamos?</strong> Revise el aviso de privacidad completo.</li>
                            </ol>
                        </div>
                    </section>

                    <section class="privacy-section">
                        <h2>&Iacute;ndice</h2>
                        <nav class="toc">
                            <ol>
                                <li>&iquest;Qu&eacute; informaci&oacute;n recopilamos?</li>
                                <li>&iquest;C&oacute;mo procesamos su informaci&oacute;n?</li>
                                <li>&iquest;En qu&eacute; bases legales nos apoyamos para procesar su informaci&oacute;n personal?</li>
                                <li>&iquest;Cu&aacute;ndo y con qui&eacute;n compartimos su informaci&oacute;n personal?</li>
                                <li>&iquest;Utilizamos cookies y otras tecnolog&iacute;as de seguimiento?</li>
                                <li>&iquest;Durante cu&aacute;nto tiempo conservamos su informaci&oacute;n?</li>
                                <li>&iquest;C&oacute;mo mantenemos segura su informaci&oacute;n?</li>
                                <li>&iquest;Recopilamos informaci&oacute;n de menores?</li>
                                <li>&iquest;Cu&aacute;les son sus derechos de privacidad?</li>
                                <li>Controles para funciones de no seguimiento</li>
                                <li>&iquest;Los residentes de Estados Unidos tienen derechos de privacidad espec&iacute;ficos?</li>
                                <li>&iquest;Realizamos actualizaciones a este aviso?</li>
                                <li>&iquest;C&oacute;mo puede contactarnos sobre este aviso?</li>
                                <li>&iquest;C&oacute;mo puede revisar, actualizar o eliminar los datos que recopilamos de usted?</li>
                            </ol>
                        </nav>
                    </section>

                    <section class="privacy-section">
                        <h2>1. &iquest;Qu&eacute; Informaci&oacute;n Recopilamos?</h2>
                        <h3>Informaci&oacute;n personal que usted nos proporciona</h3>
                        <p>
                            Recopilamos informaci&oacute;n personal que usted nos proporciona voluntariamente cuando se registra en los Servicios, expresa inter&eacute;s en obtener
                            informaci&oacute;n sobre nosotros o nuestros productos y Servicios, cuando participa en actividades en los Servicios o de otra manera cuando se comunica con nosotros.
                        </p>
                        <p>
                            <strong>Informaci&oacute;n Personal Proporcionada por Usted.</strong> La informaci&oacute;n personal que recopilamos depende del contexto de sus interacciones con
                            nosotros y los Servicios, las decisiones que tome y los productos y funciones que utilice. La informaci&oacute;n personal que recopilamos puede incluir lo siguiente:
                        </p>
                        <ul>
                            <li>Nombres</li>
                            <li>N&uacute;meros de tel&eacute;fono</li>
                            <li>Direcciones de correo electr&oacute;nico</li>
                            <li>Nombres de usuario</li>
                            <li>Contrase&ntilde;as</li>
                            <li>N&uacute;meros de tarjetas de d&eacute;bito/cr&eacute;dito</li>
                            <li>Direcciones de facturaci&oacute;n</li>
                        </ul>
                        <p><strong>Informaci&oacute;n Sensible.</strong> Cuando sea necesario, con su consentimiento o seg&uacute;n lo permita la ley aplicable, procesamos las siguientes categor&iacute;as de informaci&oacute;n sensible:</p>
                        <ul>
                            <li>N&uacute;meros de seguridad social u otros identificadores gubernamentales</li>
                        </ul>
                        <p>
                            <strong>Datos de Pago.</strong> Podemos recopilar datos necesarios para procesar su pago si realiza compras, como el n&uacute;mero de su instrumento de pago y el
                            c&oacute;digo de seguridad asociado a su instrumento de pago. Todos los datos de pago son almacenados por EBANX. Puede encontrar sus avisos de privacidad aqu&iacute;:
                            <a href="https://shieldgo.com.br/?#/payment-privacy-policy" target="_blank">https://shieldgo.com.br/?#/payment-privacy-policy</a>.
                        </p>
                        <p><strong>Datos de Aplicaciones.</strong> Si utiliza nuestras aplicaciones, tambi&eacute;n podemos recopilar la siguiente informaci&oacute;n si decide proporcionarnos acceso o permiso:</p>
                        <ul>
                            <li>Informaci&oacute;n de Geolocalizaci&oacute;n</li>
                            <li>Acceso al Dispositivo M&oacute;vil</li>
                            <li>Datos del Dispositivo M&oacute;vil</li>
                            <li>Notificaciones Push</li>
                        </ul>
                        <p>Esta informaci&oacute;n es principalmente necesaria para mantener la seguridad y operaci&oacute;n de nuestras aplicaciones, para la resoluci&oacute;n de problemas y para nuestros an&aacute;lisis internos y prop&oacute;sitos de informes.</p>
                        <p>Toda la informaci&oacute;n personal que nos proporcione debe ser verdadera, completa y precisa, y debe notificarnos sobre cualquier cambio en dicha informaci&oacute;n personal.</p>

                        <h3>Informaci&oacute;n recopilada autom&aacute;ticamente</h3>
                        <p>
                            Parte de la informaci&oacute;n &mdash; como su direcci&oacute;n de Protocolo de Internet (IP) y/o las caracter&iacute;sticas de su navegador y dispositivo &mdash; se recopila
                            autom&aacute;ticamente cuando visita nuestros Servicios.
                        </p>
                        <p>
                            Recopilamos autom&aacute;ticamente cierta informaci&oacute;n cuando visita, utiliza o navega por los Servicios. Esta informaci&oacute;n no revela su identidad espec&iacute;fica
                            (como su nombre o informaci&oacute;n de contacto), pero puede incluir informaci&oacute;n sobre el dispositivo y uso, como su direcci&oacute;n IP, caracter&iacute;sticas del
                            navegador y dispositivo, sistema operativo, preferencias de idioma, URLs de referencia, nombre del dispositivo, pa&iacute;s, ubicaci&oacute;n, informaci&oacute;n sobre c&oacute;mo
                            y cu&aacute;ndo utiliza nuestros Servicios y otra informaci&oacute;n t&eacute;cnica.
                        </p>
                        <p>Como muchas empresas, tambi&eacute;n recopilamos informaci&oacute;n a trav&eacute;s de cookies y tecnolog&iacute;as similares.</p>
                        <p>La informaci&oacute;n que recopilamos incluye:</p>
                        <ul>
                            <li>Datos de Registro y Uso</li>
                            <li>Datos del Dispositivo</li>
                            <li>Datos de Ubicaci&oacute;n</li>
                        </ul>

                        <h3>Informaci&oacute;n recopilada de otras fuentes</h3>
                        <p>
                            Para mejorar nuestra capacidad de proporcionar marketing relevante, ofertas y servicios, y actualizar nuestros registros, podemos obtener informaci&oacute;n sobre usted
                            de otras fuentes, como bases de datos p&uacute;blicas, socios de marketing, programas de afiliados, proveedores de datos y de otros terceros.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>2. &iquest;C&oacute;mo Procesamos Su Informaci&oacute;n?</h2>
                        <p>Procesamos su informaci&oacute;n personal por diversas razones, dependiendo de c&oacute;mo interact&uacute;e con nuestros Servicios. Estas razones incluyen:</p>
                        <ul>
                            <li>Para facilitar la creaci&oacute;n de cuentas y la autenticaci&oacute;n, y de otra manera gestionar las cuentas de usuario.</li>
                            <li>Para entregar y facilitar la entrega de servicios al usuario.</li>
                            <li>Para enviarle informaci&oacute;n administrativa.</li>
                            <li>Para permitir comunicaciones entre usuarios.</li>
                            <li>Para proteger nuestros Servicios.</li>
                            <li>Para salvar o proteger el inter&eacute;s vital de un individuo.</li>
                            <li>Geolocalizaci&oacute;n e informaci&oacute;n del dispositivo para la optimizaci&oacute;n y mejora del rendimiento de la aplicaci&oacute;n.</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>3. &iquest;En Qu&eacute; Bases Legales Nos Apoyamos Para Procesar Su Informaci&oacute;n?</h2>
                        <p>
                            Solo procesamos su informaci&oacute;n personal cuando creemos que es necesario y tenemos una raz&oacute;n legal v&aacute;lida (es decir, base legal) para hacerlo
                            seg&uacute;n la ley aplicable, como con su consentimiento, para cumplir con las leyes, para proporcionarle servicios para celebrar o cumplir nuestras obligaciones
                            contractuales, para proteger sus derechos o para cumplir nuestros intereses comerciales leg&iacute;timos.
                        </p>
                        <p>Las bases legales en las que nos apoyamos para procesar su informaci&oacute;n personal incluyen:</p>
                        <ul>
                            <li>Consentimiento</li>
                            <li>Ejecuci&oacute;n de un Contrato</li>
                            <li>Intereses Leg&iacute;timos</li>
                            <li>Obligaciones Legales</li>
                            <li>Intereses Vitales</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>4. &iquest;Cu&aacute;ndo Y Con Qui&eacute;n Compartimos Su Informaci&oacute;n Personal?</h2>
                        <p>Es posible que necesitemos compartir su informaci&oacute;n personal en situaciones espec&iacute;ficas descritas en esta secci&oacute;n y/o con los siguientes terceros:</p>
                        <ul>
                            <li><strong>Con proveedores de servicios:</strong> Para facilitar nuestros servicios; para proporcionar servicios en nuestro nombre; para realizar servicios relacionados; o para ayudarnos a analizar c&oacute;mo se utilizan nuestros servicios.</li>
                            <li><strong>Con otros usuarios:</strong> Cuando comparte informaci&oacute;n personal o interact&uacute;a en &aacute;reas p&uacute;blicas con otros usuarios, dicha informaci&oacute;n puede ser vista por todos los usuarios y puede distribuirse p&uacute;blicamente.</li>
                            <li><strong>Con afiliados:</strong> A nuestros afiliados para los fines descritos en esta Pol&iacute;tica de Privacidad.</li>
                            <li><strong>Con socios comerciales:</strong> Con socios comerciales de confianza que pueden usar su informaci&oacute;n personal para contactarle sobre productos y servicios relacionados.</li>
                            <li><strong>Con otros terceros:</strong> Con otros terceros para fines para los cuales hemos obtenido su consentimiento, y como se describe en esta Pol&iacute;tica de Privacidad.</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>5. &iquest;Utilizamos Cookies Y Otras Tecnolog&iacute;as De Seguimiento?</h2>
                        <p>Utilizamos cookies y tecnolog&iacute;as de seguimiento similares para recopilar y usar informaci&oacute;n personal sobre usted, incluyendo para ofrecer publicidad basada en intereses.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>6. &iquest;Durante Cu&aacute;nto Tiempo Conservamos Su Informaci&oacute;n?</h2>
                        <p>Conservamos su informaci&oacute;n personal durante el tiempo necesario para cumplir los fines descritos en esta Pol&iacute;tica de Privacidad, a menos que la ley exija o permita un per&iacute;odo de retenci&oacute;n m&aacute;s largo.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>7. &iquest;C&oacute;mo Mantenemos Segura Su Informaci&oacute;n?</h2>
                        <p>Hemos implementado medidas de seguridad t&eacute;cnicas y organizativas apropiadas dise&ntilde;adas para proteger la seguridad de cualquier informaci&oacute;n personal que procesamos.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>8. &iquest;Recopilamos Informaci&oacute;n De Menores?</h2>
                        <p>
                            No solicitamos datos a sabiendas ni comercializamos a menores de 18 a&ntilde;os. Al utilizar los Servicios, usted declara que tiene al menos 18 a&ntilde;os o que es
                            el padre o tutor de dicho menor dependiente y consiente el uso de los Servicios por parte de dicho menor dependiente.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>9. &iquest;Cu&aacute;les Son Sus Derechos De Privacidad?</h2>
                        <p>
                            En algunas regiones, usted tiene ciertos derechos bajo las leyes de protecci&oacute;n de datos aplicables. Estos pueden incluir el derecho (i) a solicitar acceso y obtener
                            una copia de su informaci&oacute;n personal, (ii) a solicitar rectificaci&oacute;n o eliminaci&oacute;n, (iii) a restringir el procesamiento de su informaci&oacute;n personal,
                            y (iv) si corresponde, a la portabilidad de datos.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>10. Controles Para Funciones De No Seguimiento</h2>
                        <p>
                            La mayor&iacute;a de los navegadores web y algunos sistemas operativos m&oacute;viles y aplicaciones m&oacute;viles incluyen una funci&oacute;n o configuraci&oacute;n de
                            No Rastrear ("DNT") que puede activar para se&ntilde;alar su preferencia de privacidad de no tener datos sobre sus actividades de navegaci&oacute;n en l&iacute;nea
                            monitoreados y recopilados. En esta etapa, no se ha finalizado ning&uacute;n est&aacute;ndar tecnol&oacute;gico uniforme para reconocer e implementar se&ntilde;ales DNT.
                            Como tal, actualmente no respondemos a las se&ntilde;ales del navegador DNT ni a ning&uacute;n otro mecanismo que comunique autom&aacute;ticamente su elecci&oacute;n de no ser rastreado en l&iacute;nea.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>11. &iquest;Los Residentes De Estados Unidos Tienen Derechos De Privacidad Espec&iacute;ficos?</h2>
                        <p>Derechos de Privacidad de California.</p>
                        <ul>
                            <li>Si tiene menos de 18 a&ntilde;os, reside en California y tiene una cuenta registrada en los Servicios, tiene derecho a solicitar la eliminaci&oacute;n de datos no deseados que publique p&uacute;blicamente en los Servicios.</li>
                            <li>Para solicitar la eliminaci&oacute;n de dichos datos, cont&aacute;ctenos utilizando la informaci&oacute;n de contacto proporcionada a continuaci&oacute;n, e incluya la direcci&oacute;n de correo electr&oacute;nico asociada a su cuenta y una declaraci&oacute;n de que reside en California.</li>
                            <li>Nos aseguraremos de que los datos no se muestren p&uacute;blicamente en los Servicios, pero tenga en cuenta que es posible que los datos no se eliminen completa o exhaustivamente de nuestros sistemas.</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>12. &iquest;Realizamos Actualizaciones A Este Aviso?</h2>
                        <p>Podemos actualizar este aviso de privacidad de vez en cuando. La versi&oacute;n actualizada se indicar&aacute; con una fecha de "Revisi&oacute;n" actualizada y la versi&oacute;n actualizada entrar&aacute; en vigor tan pronto como sea accesible.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>13. &iquest;C&oacute;mo Puede Contactarnos Sobre Este Aviso?</h2>
                        <p>
                            Si tiene preguntas o comentarios sobre este aviso, puede contactar a nuestro Oficial de Protecci&oacute;n de Datos (DPO), [Nombre del DPO], por correo electr&oacute;nico a <a href="mailto:privacy@shieldgo.com.br">privacy@shieldgo.com.br</a> o por correo postal a:
                        </p>
                        <div class="contact-card">
                            <p>Grasp Sistemas e Comercio Ltda</p>
                            <p>Attn: Oficial de Protecci&oacute;n de Datos</p>
                            <p>Rua Rui Barbosa, 123</p>
                            <p>Sao Paulo, SP 01026-000</p>
                            <p>Brasil</p>
                        </div>
                    </section>

                    <section class="privacy-section">
                        <h2>14. &iquest;C&oacute;mo Puede Revisar, Actualizar O Eliminar Los Datos Que Recopilamos De Usted?</h2>
                        <p>
                            Seg&uacute;n las leyes aplicables de su pa&iacute;s, puede tener derecho a solicitar acceso a la informaci&oacute;n personal que recopilamos de usted, cambiar esa
                            informaci&oacute;n o eliminarla. Para solicitar revisar, actualizar o eliminar su informaci&oacute;n personal, complete y env&iacute;e una solicitud de acceso del titular de los datos.
                        </p>
                    </section>
                </div>

                <!-- Portuguese Content -->
                <div v-else class="privacy-content">
                    <section class="privacy-section">
                        <p>
                            Este aviso de privacidade da Grasp Sistemas e Com&eacute;rcio Ltda (fazendo neg&oacute;cios como Grasp) ("n&oacute;s", "nos" ou "nosso") descreve como e por que
                            podemos coletar, armazenar, usar e/ou compartilhar ("processar") suas informa&ccedil;&otilde;es quando voc&ecirc; usa nossos servi&ccedil;os ("Servi&ccedil;os"),
                            como quando voc&ecirc;:
                        </p>
                        <ul>
                            <li>Visita nosso site em <a href="https://shieldgo.com.br/" target="_blank">https://shieldgo.com.br</a>, ou qualquer site nosso que se vincule a este aviso de privacidade</li>
                            <li>Baixa e usa nosso aplicativo m&oacute;vel (ShieldGo), ou qualquer outro aplicativo nosso que se vincule a este aviso de privacidade</li>
                            <li>Interage conosco de outras maneiras relacionadas, incluindo quaisquer vendas, marketing ou eventos</li>
                        </ul>
                        <p>
                            Tem d&uacute;vidas ou preocupa&ccedil;&otilde;es? Ler este aviso de privacidade ajudar&aacute; voc&ecirc; a entender seus direitos e op&ccedil;&otilde;es de privacidade.
                            Se voc&ecirc; n&atilde;o concorda com nossas pol&iacute;ticas e pr&aacute;ticas, por favor, n&atilde;o use nossos Servi&ccedil;os. Se ainda tiver alguma d&uacute;vida ou
                            preocupa&ccedil;&atilde;o, entre em contato conosco em <a href="mailto:privacy@shieldgo.com.br">privacy@shieldgo.com.br</a>.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>Resumo dos Pontos Chave</h2>
                        <div class="key-points">
                            <p>
                                Este resumo fornece pontos chave do nosso aviso de privacidade, mas voc&ecirc; pode encontrar mais detalhes sobre qualquer um desses t&oacute;picos clicando no
                                link a seguir a cada ponto chave ou usando nosso &iacute;ndice abaixo para encontrar a se&ccedil;&atilde;o que est&aacute; procurando.
                            </p>
                        </div>
                    </section>

                    <section class="privacy-section">
                        <h2>&Iacute;ndice</h2>
                        <nav class="toc">
                            <ol>
                                <li>Que informa&ccedil;&otilde;es coletamos?</li>
                                <li>Como processamos suas informa&ccedil;&otilde;es?</li>
                                <li>Em quais bases legais nos apoiamos para processar suas informa&ccedil;&otilde;es pessoais?</li>
                                <li>Quando e com quem compartilhamos suas informa&ccedil;&otilde;es pessoais?</li>
                                <li>Usamos cookies e outras tecnologias de rastreamento?</li>
                                <li>Por quanto tempo guardamos suas informa&ccedil;&otilde;es?</li>
                                <li>Como mantemos suas informa&ccedil;&otilde;es seguras?</li>
                                <li>Coletamos informa&ccedil;&otilde;es de menores?</li>
                                <li>Quais s&atilde;o seus direitos de privacidade?</li>
                                <li>Controles para recursos de n&atilde;o rastreamento</li>
                                <li>Residentes dos Estados Unidos t&ecirc;m direitos de privacidade espec&iacute;ficos?</li>
                                <li>Fazemos atualiza&ccedil;&otilde;es neste aviso?</li>
                                <li>Como voc&ecirc; pode entrar em contato conosco sobre este aviso?</li>
                                <li>Como voc&ecirc; pode revisar, atualizar ou excluir os dados que coletamos de voc&ecirc;?</li>
                            </ol>
                        </nav>
                    </section>

                    <section class="privacy-section">
                        <h2>1. Que Informa&ccedil;&otilde;es Coletamos?</h2>
                        <h3>Informa&ccedil;&otilde;es pessoais que voc&ecirc; nos fornece</h3>
                        <p>
                            Coletamos informa&ccedil;&otilde;es pessoais que voc&ecirc; fornece voluntariamente quando se registra nos Servi&ccedil;os, expressa interesse em obter
                            informa&ccedil;&otilde;es sobre n&oacute;s ou nossos produtos e Servi&ccedil;os, quando participa de atividades nos Servi&ccedil;os ou de outra forma quando entra
                            em contato conosco.
                        </p>
                        <p>
                            <strong>Informa&ccedil;&otilde;es Pessoais Fornecidas por Voc&ecirc;.</strong> As informa&ccedil;&otilde;es pessoais que coletamos dependem do contexto de suas
                            intera&ccedil;&otilde;es conosco e com os Servi&ccedil;os, as escolhas que voc&ecirc; faz e os produtos e recursos que voc&ecirc; usa. As
                            informa&ccedil;&otilde;es pessoais que coletamos podem incluir o seguinte:
                        </p>
                        <ul>
                            <li>Nomes</li>
                            <li>N&uacute;meros de telefone</li>
                            <li>Endere&ccedil;os de e-mail</li>
                            <li>Nomes de usu&aacute;rio</li>
                            <li>Senhas</li>
                            <li>N&uacute;meros de cart&otilde;es de d&eacute;bito/cr&eacute;dito</li>
                            <li>Endere&ccedil;os de cobran&ccedil;a</li>
                        </ul>
                        <p>
                            <strong>Informa&ccedil;&otilde;es Sens&iacute;veis.</strong> Quando necess&aacute;rio, com seu consentimento ou conforme permitido pela lei aplic&aacute;vel,
                            processamos as seguintes categorias de informa&ccedil;&otilde;es sens&iacute;veis:
                        </p>
                        <ul>
                            <li>N&uacute;meros de seguran&ccedil;a social ou outros identificadores governamentais</li>
                        </ul>
                        <p>
                            <strong>Dados de Pagamento.</strong> Podemos coletar dados necess&aacute;rios para processar seu pagamento se voc&ecirc; fizer compras, como o n&uacute;mero do
                            seu instrumento de pagamento e o c&oacute;digo de seguran&ccedil;a associado ao seu instrumento de pagamento. Todos os dados de pagamento s&atilde;o armazenados
                            pelo EBANX. Voc&ecirc; pode encontrar seus avisos de privacidade aqui:
                            <a href="https://shieldgo.com.br/?#/payment-privacy-policy" target="_blank">https://shieldgo.com.br/?#/payment-privacy-policy</a>.
                        </p>
                        <p>
                            <strong>Dados de Aplicativos.</strong> Se voc&ecirc; usar nossos aplicativos, tamb&eacute;m podemos coletar as seguintes informa&ccedil;&otilde;es, caso
                            voc&ecirc; escolha nos fornecer acesso ou permiss&atilde;o:
                        </p>
                        <ul>
                            <li>Informa&ccedil;&otilde;es de Geolocaliza&ccedil;&atilde;o</li>
                            <li>Acesso ao Dispositivo M&oacute;vel</li>
                            <li>Dados do Dispositivo M&oacute;vel</li>
                            <li>Notifica&ccedil;&otilde;es Push</li>
                        </ul>
                        <p>
                            Essas informa&ccedil;&otilde;es s&atilde;o principalmente necess&aacute;rias para manter a seguran&ccedil;a e opera&ccedil;&atilde;o de nossos aplicativos, para
                            solu&ccedil;&atilde;o de problemas e para nossas an&aacute;lises internas e prop&oacute;sitos de relat&oacute;rios.
                        </p>
                        <p>
                            Todas as informa&ccedil;&otilde;es pessoais que voc&ecirc; nos fornece devem ser verdadeiras, completas e precisas, e voc&ecirc; deve nos notificar sobre
                            quaisquer altera&ccedil;&otilde;es nessas informa&ccedil;&otilde;es pessoais.
                        </p>

                        <h3>Informa&ccedil;&otilde;es coletadas automaticamente</h3>
                        <p>
                            Coletamos automaticamente certas informa&ccedil;&otilde;es quando voc&ecirc; visita, usa ou navega nos Servi&ccedil;os. Essas informa&ccedil;&otilde;es n&atilde;o
                            revelam sua identidade espec&iacute;fica (como seu nome ou informa&ccedil;&otilde;es de contato), mas podem incluir informa&ccedil;&otilde;es sobre o dispositivo
                            e uso, como seu endere&ccedil;o IP, caracter&iacute;sticas do navegador e do dispositivo, sistema operacional, prefer&ecirc;ncias de idioma, URLs de
                            refer&ecirc;ncia, nome do dispositivo, pa&iacute;s, localiza&ccedil;&atilde;o, informa&ccedil;&otilde;es sobre como e quando voc&ecirc; usa nossos Servi&ccedil;os
                            e outras informa&ccedil;&otilde;es t&eacute;cnicas.
                        </p>
                        <p>Como muitos neg&oacute;cios, tamb&eacute;m coletamos informa&ccedil;&otilde;es por meio de cookies e tecnologias similares.</p>
                        <p>As informa&ccedil;&otilde;es que coletamos incluem:</p>
                        <ul>
                            <li>Dados de Log e Uso</li>
                            <li>Dados do Dispositivo</li>
                            <li>Dados de Localiza&ccedil;&atilde;o</li>
                        </ul>

                        <h3>Informa&ccedil;&otilde;es coletadas de outras fontes</h3>
                        <p>
                            Para aprimorar nossa capacidade de fornecer marketing relevante, ofertas e servi&ccedil;os a voc&ecirc; e atualizar nossos registros, podemos obter
                            informa&ccedil;&otilde;es sobre voc&ecirc; de outras fontes, como bancos de dados p&uacute;blicos, parceiros de marketing, programas de afiliados, provedores de
                            dados e de outras terceiras partes.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>2. Como Processamos Suas Informa&ccedil;&otilde;es?</h2>
                        <p>Processamos suas informa&ccedil;&otilde;es pessoais por uma variedade de raz&otilde;es, dependendo de como voc&ecirc; interage com nossos Servi&ccedil;os, incluindo:</p>
                        <ul>
                            <li>Para facilitar a cria&ccedil;&atilde;o de conta e autentica&ccedil;&atilde;o e de outra forma gerenciar contas de usu&aacute;rios.</li>
                            <li>Para entregar e facilitar a entrega de servi&ccedil;os ao usu&aacute;rio.</li>
                            <li>Para enviar informa&ccedil;&otilde;es administrativas para voc&ecirc;.</li>
                        </ul>
                    </section>

                    <section class="privacy-section">
                        <h2>3. Quais Bases Legais Nos Apoiamos Para Processar Suas Informa&ccedil;&otilde;es Pessoais?</h2>
                        <p>
                            S&oacute; processamos suas informa&ccedil;&otilde;es pessoais quando acreditamos que &eacute; necess&aacute;rio e temos uma raz&atilde;o legal
                            v&aacute;lida para faz&ecirc;-lo sob a lei aplic&aacute;vel, como com seu consentimento, para cumprir leis, para fornecer-lhe
                            servi&ccedil;os para entrar em ou cumprir nossas obriga&ccedil;&otilde;es contratuais, para proteger seus direitos ou para cumprir nossos interesses comerciais
                            leg&iacute;timos.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>4. Quando E Com Quem Compartilhamos Suas Informa&ccedil;&otilde;es Pessoais?</h2>
                        <p>Podemos compartilhar informa&ccedil;&otilde;es em situa&ccedil;&otilde;es espec&iacute;ficas descritas nesta se&ccedil;&atilde;o e/ou com os seguintes terceiros.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>5. Usamos Cookies E Outras Tecnologias De Rastreamento?</h2>
                        <p>Podemos usar cookies e outras tecnologias de rastreamento para coletar e armazenar suas informa&ccedil;&otilde;es.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>6. Por Quanto Tempo Guardamos Suas Informa&ccedil;&otilde;es?</h2>
                        <p>
                            Mantemos suas informa&ccedil;&otilde;es pelo tempo necess&aacute;rio para cumprir os prop&oacute;sitos delineados neste aviso de privacidade, a menos
                            que seja necess&aacute;rio um per&iacute;odo de reten&ccedil;&atilde;o mais longo conforme exigido ou permitido por lei.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>7. Como Mantemos Suas Informa&ccedil;&otilde;es Seguras?</h2>
                        <p>Nosso objetivo &eacute; proteger suas informa&ccedil;&otilde;es pessoais por meio de um sistema de medidas de seguran&ccedil;a organizacionais e t&eacute;cnicas.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>8. Coletamos Informa&ccedil;&otilde;es De Menores?</h2>
                        <p>N&atilde;o coletamos dados de menores de 18 anos de idade.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>9. Quais S&atilde;o Seus Direitos De Privacidade?</h2>
                        <p>
                            Em algumas regi&otilde;es, como a &Aacute;rea Econ&ocirc;mica Europeia (EEA), Reino Unido (UK), Su&iacute;&ccedil;a e Canad&aacute;, voc&ecirc; tem
                            direitos que permitem maior acesso e controle sobre suas informa&ccedil;&otilde;es pessoais.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>10. Controles Para Recursos De N&atilde;o Rastreamento</h2>
                        <p>
                            A maioria dos navegadores da web e alguns sistemas operacionais m&oacute;veis e aplicativos m&oacute;veis incluem um recurso ou configura&ccedil;&atilde;o de
                            N&atilde;o Rastrear ("DNT") que voc&ecirc; pode ativar para sinalizar sua prefer&ecirc;ncia de privacidade para n&atilde;o ter dados sobre suas atividades de
                            navega&ccedil;&atilde;o online monitoradas e coletadas.
                        </p>
                    </section>

                    <section class="privacy-section">
                        <h2>11. Residentes Dos Estados Unidos T&ecirc;m Direitos De Privacidade Espec&iacute;ficos?</h2>
                        <p>Se voc&ecirc; for residente dos Estados Unidos, tem direitos espec&iacute;ficos em rela&ccedil;&atilde;o ao acesso &agrave;s suas informa&ccedil;&otilde;es pessoais.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>12. Fazemos Atualiza&ccedil;&otilde;es Neste Aviso?</h2>
                        <p>Sim, atualizaremos este aviso conforme necess&aacute;rio para permanecer em conformidade com as leis relevantes.</p>
                    </section>

                    <section class="privacy-section">
                        <h2>13. Como Voc&ecirc; Pode Entrar Em Contato Conosco Sobre Este Aviso?</h2>
                        <p>
                            Se tiver d&uacute;vidas ou coment&aacute;rios sobre este aviso, voc&ecirc; pode entrar em contato com nosso Oficial de Prote&ccedil;&atilde;o de Dados (DPO),
                            Thiago Teixeira Simon, por e-mail em <a href="mailto:thiagosimon@grasp.com.br">thiagosimon@grasp.com.br</a>, por telefone no +55 11 972194501, ou entrar em contato conosco por correio em:
                        </p>
                        <div class="contact-card">
                            <p>Grasp Sistemas e Com&eacute;rcio Ltda</p>
                            <p>Thiago Teixeira Simon</p>
                            <p>Rua Gabriele D'Annunzio, 132 - Campo Belo</p>
                            <p>S&atilde;o Paulo - SP, 04619-000</p>
                            <p>Brasil</p>
                        </div>
                    </section>

                    <section class="privacy-section">
                        <h2>14. Como Voc&ecirc; Pode Revisar, Atualizar Ou Excluir Os Dados Que Coletamos De Voc&ecirc;?</h2>
                        <p>
                            Com base nas leis aplic&aacute;veis do seu pa&iacute;s, voc&ecirc; pode ter o direito de solicitar o acesso &agrave;s informa&ccedil;&otilde;es pessoais que coletamos de voc&ecirc;, alterar essas informa&ccedil;&otilde;es
                            ou exclu&iacute;-las. Para solicitar a revis&atilde;o, atualiza&ccedil;&atilde;o ou exclus&atilde;o das suas informa&ccedil;&otilde;es pessoais, por favor, preencha e envie um pedido de acesso do titular dos dados.
                        </p>
                    </section>
                </div>

                <!-- Footer logo -->
                <div class="privacy-footer">
                    <a href="https://admin.shieldgo.com.br/">
                        <img src="../../assets/images/logo.png" alt="ShieldGo" width="120" />
                    </a>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import AppOptions from '../../config/AppOptions.vue'

export default {
    name: 'PrivacyPolicy',
    created() {
        AppOptions.appEmpty = true
    },
    data() {
        return {
            language: 'en',
        }
    },
    methods: {
        changeLanguage(language) {
            this.language = language
        },
    },
}
</script>

<style lang="scss" scoped>
.privacy-page {
    min-height: 100vh;
    background-color: #000;
    color: rgba(255, 255, 255, 0.85);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

// Header
.privacy-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.header-content {
    max-width: 860px;
    margin: 0 auto;
    padding: 16px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.header-logo img {
    opacity: 0.9;
    transition: opacity 0.2s;

    &:hover {
        opacity: 1;
    }
}

.language-selector {
    display: flex;
    gap: 6px;

    button {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 14px;
        border-radius: 8px;
        border: 1px solid rgba(255, 255, 255, 0.08);
        background: #1d1c20;
        color: rgba(255, 255, 255, 0.5);
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;

        .flag-icon {
            font-size: 16px;
        }

        &:hover {
            border-color: rgba(255, 255, 255, 0.15);
            color: rgba(255, 255, 255, 0.8);
        }

        &.active {
            background: var(--app-theme, #dc3545);
            border-color: var(--app-theme, #dc3545);
            color: #fff;
        }
    }
}

// Main content
.privacy-main {
    padding: 0 32px 80px;
}

.privacy-container {
    max-width: 860px;
    margin: 0 auto;
}

// Title section
.privacy-title-section {
    text-align: center;
    padding: 60px 0 48px;
}


h1 {
    font-size: 36px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 12px;
    letter-spacing: -0.02em;
}

.last-updated {
    color: rgba(255, 255, 255, 0.4);
    font-size: 14px;
    margin: 0;
}

// Content
.privacy-content {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.privacy-section {
    background: #1d1c20;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 14px;
    padding: 32px;
    margin-bottom: 16px;
    transition: border-color 0.2s;

    &:hover {
        border-color: rgba(255, 255, 255, 0.1);
    }

    h2 {
        font-size: 20px;
        font-weight: 600;
        color: #fff;
        margin: 0 0 20px;
        padding-bottom: 14px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        letter-spacing: -0.01em;
    }

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.9);
        margin: 24px 0 12px;

        &:first-of-type {
            margin-top: 0;
        }
    }

    p {
        font-size: 14px;
        line-height: 1.75;
        color: rgba(255, 255, 255, 0.6);
        margin: 0 0 14px;

        &:last-child {
            margin-bottom: 0;
        }

        strong {
            color: rgba(255, 255, 255, 0.85);
        }
    }

    a {
        color: var(--app-theme, #dc3545);
        text-decoration: none;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.8;
            text-decoration: underline;
        }
    }

    ul, ol {
        padding-left: 24px;
        margin: 0 0 14px;

        &:last-child {
            margin-bottom: 0;
        }

        li {
            font-size: 14px;
            line-height: 1.75;
            color: rgba(255, 255, 255, 0.6);
            margin-bottom: 6px;
            padding-left: 4px;

            strong {
                color: rgba(255, 255, 255, 0.85);
            }

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}

// Key points card
.key-points {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 10px;
    padding: 20px 24px;
    border: 1px solid rgba(255, 255, 255, 0.04);

    ol {
        margin: 0;

        li {
            padding: 6px 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.04);

            &:last-child {
                border-bottom: none;
                padding-bottom: 0;
            }

            &:first-child {
                padding-top: 0;
            }
        }
    }
}

// Table of contents
.toc {
    ol {
        margin: 0;
        counter-reset: toc-counter;
        list-style: none;
        padding-left: 0;

        li {
            counter-increment: toc-counter;
            padding: 10px 16px;
            border-radius: 8px;
            transition: background 0.2s;
            cursor: default;

            &::before {
                content: counter(toc-counter) ".";
                color: var(--app-theme, #dc3545);
                font-weight: 600;
                margin-right: 12px;
                min-width: 24px;
                display: inline-block;
            }

            &:hover {
                background: rgba(255, 255, 255, 0.03);
            }
        }
    }
}

// Contact card
.contact-card {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    padding: 20px 24px;
    margin-top: 12px;

    p {
        margin: 0 0 4px;
        font-size: 14px;
        color: rgba(255, 255, 255, 0.6);

        &:first-child {
            color: #fff;
            font-weight: 600;
        }

        &:last-child {
            margin-bottom: 0;
        }
    }
}

// Footer
.privacy-footer {
    text-align: center;
    padding: 48px 0 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    margin-top: 32px;

    img {
        opacity: 0.5;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.8;
        }
    }
}

// Responsive
@media (max-width: 768px) {
    .privacy-main {
        padding: 0 16px 60px;
    }

    .header-content {
        padding: 14px 16px;
    }

    .privacy-title-section {
        padding: 40px 0 32px;
    }

    h1 {
        font-size: 28px;
    }

    .privacy-section {
        padding: 24px 20px;
        border-radius: 12px;
    }

}
</style>
